import LoaderInline from '@jetbrains/ring-ui/components/loader-inline/loader-inline'
import classNames from 'classnames'
import {memo, Suspense, useDeferredValue, useMemo} from 'react'
import {type EntryPointProps, graphql, useFragment} from 'react-relay'
import type {PreloadedQuery} from 'react-relay/relay-hooks/EntryPointTypes'
import {useLocation} from 'react-router-dom'

import {useDebounceValue} from '../../../../../hooks/useDebounceValue'
import type {BuildId} from '../../../../../types'
import Grid from '../../../../common/Grid/Grid'
import {TestFilterStatus} from '../TestsFilter/TestsStatusFilter/TestsStatusFilter.utils'

import type {TestScopesView$key} from './__generated__/TestScopesView.graphql'
import type {TestScopesViewCountsFragment$key} from './__generated__/TestScopesViewCountsFragment.graphql'
import type {TestScopesViewQuery} from './__generated__/TestScopesViewQuery.graphql'
import TestScope from './TestScope/TestScope'
import TestScopesViewPager from './TestScopesPager/TestScopesPager'
import {getScopesViewVariables} from './TestScopesView.utils'
import TestScopesViewHeader from './TestScopesViewHeader/TestScopesViewHeader'
import {useTestScopes} from './useQueryTestScopes'

import styles from './TestScopesView.css'

export type Props = {
  buildId: BuildId
  countsKey: TestScopesViewCountsFragment$key | null
}

type Queries = {testScopes: TestScopesViewQuery}

const fragment = graphql`
  fragment TestScopesView on TestScopes {
    nextHref
    testScope {
      suite
      package
      class
      ...TestScope
    }
  }
`

const countsFragment = graphql`
  fragment TestScopesViewCountsFragment on TestCounters {
    success
    failed
    muted
    ignored
  }
`

function TestScopesViewComponent(props: {
  buildId: BuildId
  countsKey: TestScopesViewCountsFragment$key | null
  testScopePreloadedQuery: PreloadedQuery<TestScopesViewQuery>
  isLoading: boolean
}) {
  const {buildId, countsKey, isLoading} = props
  const location = useLocation()

  const {locator, status, scopeName} = useMemo(
    () => getScopesViewVariables({location, buildLocator: `id:${buildId}`}),
    [buildId, location],
  )

  const counts = useFragment(countsFragment, countsKey)
  const success = counts?.success ?? 0
  const failed = counts?.failed ?? 0
  const muted = counts?.muted ?? 0
  const ignored = counts?.ignored ?? 0

  const testScopes = useTestScopes({
    scopeName,
    locator,
    buildId,
    preloadedQuery: props.testScopePreloadedQuery,
  })
  const testScopesData = useFragment<TestScopesView$key>(fragment, testScopes)

  const withCountersColumns = status === TestFilterStatus.ALL

  return (
    testScopesData != null && (
      <>
        <Grid
          className={classNames(styles.grid, {
            [styles.withSuccessColumn]: success > 0,
            [styles.withFailureColumn]: failed > 0,
            [styles.withMutedColumn]: muted > 0,
            [styles.withIgnoredColumn]: ignored > 0,
            [styles.withCountersColumns]: withCountersColumns,
            [styles.withOverlay]: isLoading,
          })}
        >
          <TestScopesViewHeader withCountersColumns={withCountersColumns} />
          {testScopesData.testScope?.map(scope => (
            <TestScope
              key={[scope.suite, scope.package, scope.class].join('/')}
              scopeName={scopeName}
              scopeKey={scope}
              withCountersColumns={withCountersColumns}
            />
          ))}
        </Grid>
        <TestScopesViewPager testScopesData={testScopesData} isLoading={isLoading} />
      </>
    )
  )
}

export const TestScopesView = memo(TestScopesViewComponent)

const STATE_DEBOUNCE = 500 //ms
function TestScopesViewContainer({props, queries}: EntryPointProps<Queries, {}, Props, {}>) {
  const {buildId, countsKey} = props

  const deferedPreloadedQuery = useDeferredValue(queries.testScopes)
  const isLoading = deferedPreloadedQuery !== queries.testScopes

  // We use a debounced isLoading state to minimize flickering.
  // When the debounced isLoading is true but the immediate isLoading is false,
  // we avoid showing the loader to prevent displaying it during brief loading inconsistencies.
  const debouncedLoading = useDebounceValue(isLoading, STATE_DEBOUNCE) && isLoading

  return (
    <Suspense fallback={<LoaderInline>{'Loading...'}</LoaderInline>}>
      <TestScopesView
        buildId={buildId}
        countsKey={countsKey}
        testScopePreloadedQuery={deferedPreloadedQuery}
        isLoading={debouncedLoading}
      />
    </Suspense>
  )
}

export default memo(TestScopesViewContainer)
export type TestScopesViewContainerComponent = typeof TestScopesViewContainer
